import type {
  BreadcrumbInput,
  CapturedEvent,
  ErrorContext,
  ErrorLevel,
  ErrorUser,
  IErrorTrackingProvider,
} from './types';

const MAX_EVENTS = 100;

function rid(): string {
  return Math.random().toString(36).slice(2, 10);
}

export class NoopErrorTrackingProvider implements IErrorTrackingProvider {
  readonly name = 'noop';
  readonly isActive = false;
  private events: CapturedEvent[] = [];
  private user: ErrorUser | null = null;

  captureException(error: unknown, context?: ErrorContext): string {
    const err = error instanceof Error ? error : new Error(String(error));
    const ev: CapturedEvent = {
      id: rid(),
      message: err.message,
      level: context?.level ?? 'error',
      timestamp: Date.now(),
      tags: context?.tags,
      stack: err.stack,
    };
    this.push(ev);
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.error('[errors:noop]', err, context);
    }
    return ev.id;
  }

  captureMessage(message: string, level: ErrorLevel = 'info', context?: ErrorContext): string {
    const ev: CapturedEvent = {
      id: rid(),
      message,
      level,
      timestamp: Date.now(),
      tags: context?.tags,
    };
    this.push(ev);
    return ev.id;
  }

  addBreadcrumb(_crumb: BreadcrumbInput): void {
    // no-op; breadcrumbs are not retained in the noop provider
  }

  setUser(user: ErrorUser | null): void {
    this.user = user;
  }

  getRecentEvents(limit = 25): CapturedEvent[] {
    return this.events.slice(0, limit);
  }

  private push(ev: CapturedEvent): void {
    this.events.unshift(ev);
    if (this.events.length > MAX_EVENTS) this.events.length = MAX_EVENTS;
  }
}
